import { Alert, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useAuth } from "@/providers";
import { McpTokenRecord } from "@/shared/domain/mcp";
import { useDisconnectMcpServer } from "@/shared/domain/mcp/hooks/useDisconnectMcpServer";
import { useMcpServers } from "@/shared/domain/mcp/hooks/useMcpServers";
import {
  mcpTokenStatusesQueryKey,
  useMcpTokenStatuses,
} from "@/shared/domain/mcp/hooks/useMcpTokenStatuses";
import { useStartOAuthFlow } from "@/shared/domain/mcp/hooks/useStartOAuthFlow";
import { Button } from "@/shared/foundation/primitives";

import styles from "./McpConnections.module.css";
import { MCP_OAUTH_MESSAGES } from "./mcpOAuthMessages";
import { McpServerCard } from "./McpServerCard";

export function McpConnections() {
  const { currentOrganization, currentUser, orgRoles } = useAuth();
  const orgId = currentOrganization?.org_id;

  const currentUserRole = React.useMemo(() => {
    if (!orgRoles || !currentUser) {
      return undefined;
    }
    return orgRoles.find((r) => r.user.user_id === currentUser.user_id);
  }, [orgRoles, currentUser]);

  const isOrgAdmin =
    currentUserRole?.roles?.includes("admin") ||
    currentUserRole?.roles?.includes("owner") ||
    false;

  const [connectingServerId, setConnectingServerId] = React.useState<
    string | null
  >(null);
  const [oauthError, setOauthError] = React.useState<string | null>(null);

  const { data: servers, isLoading: serversLoading } = useMcpServers(orgId);
  const { data: tokens } = useMcpTokenStatuses(orgId);
  const startOAuthMutation = useStartOAuthFlow(orgId);
  const disconnectMutation = useDisconnectMcpServer(orgId);
  const queryClient = useQueryClient();

  // The popup reports both outcomes here, and this listener is the token statuses' only
  // invalidator — the popup itself never touches the cache. The remaining gap: a popup
  // dismissed before it reports leaves no signal at all (there is no abandonment poll for
  // MCP), so a completed-then-closed authorization only surfaces on the next refetch.
  React.useEffect(() => {
    const handler = (
      event: MessageEvent<{ type?: string; message?: string }>,
    ) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data?.type === MCP_OAUTH_MESSAGES.success) {
        setOauthError(null);
        void queryClient.invalidateQueries({
          queryKey: mcpTokenStatusesQueryKey(orgId),
        });
      } else if (event.data?.type === MCP_OAUTH_MESSAGES.error) {
        setOauthError(
          event.data.message ?? "Authorization failed. Please try again.",
        );
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [orgId, queryClient]);

  const ownTokens = React.useMemo(() => {
    const own = new Map<string, McpTokenRecord>();
    if (tokens && currentUser) {
      for (const token of tokens) {
        if (token.user_id === currentUser.user_id) {
          own.set(token.server_id, token);
        }
      }
    }
    return own;
  }, [tokens, currentUser]);

  const handleConnect = async (serverId: string) => {
    // A retry starts clean; the previous attempt's banner would otherwise linger.
    setOauthError(null);
    setConnectingServerId(serverId);
    try {
      const { authorization_url } = await startOAuthMutation.mutateAsync({
        serverId,
      });
      // Open authorization URL in a popup
      const popup = window.open(
        authorization_url,
        "mcp-oauth",
        "width=600,height=700,popup=yes",
      );
      if (!popup) {
        // Popup blocked — fall back to redirect
        window.location.href = authorization_url;
      }
    } catch {
      // Error handled by mutation
    } finally {
      setConnectingServerId(null);
    }
  };

  const handleDisconnect = (serverId: string) => {
    disconnectMutation.mutate(serverId);
  };

  return (
    <div>
      <div className={styles.header}>
        <Typography variant="body2" color="text.secondary">
          Connect external services to use their tools in AI agent threads. Each
          user must authenticate with their own account.
        </Typography>
        <Tooltip
          title="Contact support@roboto.ai if you're interested in connecting custom MCP servers."
          placement="bottom"
          arrow
        >
          <span>
            <Button variant="contained" disabled>
              Add Server
            </Button>
          </span>
        </Tooltip>
      </div>

      {serversLoading && <CircularProgress />}

      {startOAuthMutation.isError && (
        <Alert severity="error" className={styles.alert}>
          Failed to start authorization. Please try again.
        </Alert>
      )}

      {oauthError !== null && (
        <Alert severity="error" className={styles.alert}>
          {oauthError}
        </Alert>
      )}

      {!serversLoading && servers && servers.length > 0 ? (
        <div className={styles.serverList}>
          {servers.map((server) => (
            <McpServerCard
              key={server.server_id}
              server={server}
              orgId={orgId}
              token={ownTokens.get(server.server_id)}
              currentUserId={currentUser?.user_id}
              isOrgAdmin={isOrgAdmin}
              onConnect={(id) => void handleConnect(id)}
              onDisconnect={handleDisconnect}
              connectLoading={connectingServerId === server.server_id}
            />
          ))}
        </div>
      ) : (
        !serversLoading && (
          <div className={styles.emptyState}>
            <Typography variant="body1" color="text.secondary">
              No MCP servers available yet.
            </Typography>
          </div>
        )
      )}
    </div>
  );
}
